/**
 * FilterBar — compact horizontal filter bar for non-map views.
 *
 * Each filter category is a dropdown button showing active count.
 * Clicking opens a popover with chip toggles. Sits in normal document
 * flow at the top of list/matrix/dashboard content areas.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useAdjacencyStore } from '../../store/adjacency-store';
import type {
  StrategicInitiative,
  Status,
  Phase,
  DataSource,
  AdjacencyAxis,
  RelationshipType,
} from '../../types/adjacency';
import { INITIATIVE_COLORS, STATUS_COLORS, STATUS_DEFAULT_COLOR } from '../../utils/map-colors';
import { AXIS_COLORS, PHASE_COLORS, SOURCE_COLORS, SOURCE_LABELS } from '../../utils/view-colors';
import { getAllPhases, getAllStatuses } from '../../utils/reference-data';
import { FilterChip } from '../map/FilterChip';

// ── Value arrays ─────────────────────────────────────────────────────

const ALL_AXES: AdjacencyAxis[] = [
  'Core', 'New Services', 'New Markets & Customers', 'Forward Integration',
  'New Technologies & Capabilities', 'New Channels', 'New Geographies',
  'Backward Integration', 'New Products',
];

const ALL_INITIATIVES: StrategicInitiative[] = [
  'US Mining', 'Proximity Detection', 'Analytics', 'Maritime',
  'International Mining', 'New Verticals', 'Capabilities', 'Other',
];


const ALL_SOURCES: DataSource[] = ['priority-list', 'adjacency-map', 'manual'];

const ALL_REL_TYPES: RelationshipType[] = ['parent-child', 'related'];

const REL_TYPE_LABELS: Record<RelationshipType, string> = {
  'parent-child': 'Parent-Child',
  'related': 'Related',
};

const REL_TYPE_COLORS: Record<RelationshipType, string> = {
  'parent-child': '#3b82f6',
  'related': '#f59e0b',
};

// ── Component ────────────────────────────────────────────────────────

export function FilterBar() {
  const { filters, setFilters, resetFilters, nodes } = useAdjacencyStore();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Dynamic Phase/Status options — includes any SP-introduced values
  const allPhases = useMemo(() => getAllPhases(nodes), [nodes]);
  const allStatuses = useMemo(() => getAllStatuses(nodes), [nodes]);

  const hasActiveFilters =
    filters.axes.length > 0 ||
    filters.initiatives.length > 0 ||
    filters.statuses.length > 0 ||
    filters.phases.length > 0 ||
    filters.sources.length > 0 ||
    filters.relationshipFilter.length > 0;

  const closeAll = useCallback(() => setOpenDropdown(null), []);

  return (
    <div
      className="glass-subtle rounded-lg px-3 py-1.5 mb-2 flex items-center gap-1.5 flex-wrap relative z-40"
      style={{ borderColor: 'var(--glass-border)' }}
    >
      <DropdownFilter
        label="Axis"
        activeCount={filters.axes.length === 0 ? ALL_AXES.length : filters.axes.length}
        totalCount={ALL_AXES.length}
        isOpen={openDropdown === 'axis'}
        onToggle={() => setOpenDropdown(openDropdown === 'axis' ? null : 'axis')}
        onClose={closeAll}
        onAll={() => setFilters({ axes: [] })}
        onNone={() => setFilters({ axes: ['__none__' as AdjacencyAxis] })}
      >
        {ALL_AXES.map((axis) => (
          <FilterChip
            key={axis}
            label={axis === 'New Technologies & Capabilities' ? 'New Tech' : axis}
            color={AXIS_COLORS[axis]}
            isActive={filters.axes.length === 0 || filters.axes.includes(axis)}
            onToggle={() => toggleFilter('axes', axis, ALL_AXES, filters.axes, setFilters)}
          />
        ))}
      </DropdownFilter>

      <DropdownFilter
        label="Initiative"
        activeCount={filters.initiatives.length === 0 ? ALL_INITIATIVES.length : filters.initiatives.length}
        totalCount={ALL_INITIATIVES.length}
        isOpen={openDropdown === 'initiative'}
        onToggle={() => setOpenDropdown(openDropdown === 'initiative' ? null : 'initiative')}
        onClose={closeAll}
        onAll={() => setFilters({ initiatives: [] })}
        onNone={() => setFilters({ initiatives: ['__none__' as StrategicInitiative] })}
      >
        {ALL_INITIATIVES.map((init) => (
          <FilterChip
            key={init}
            label={init}
            color={INITIATIVE_COLORS[init]}
            isActive={filters.initiatives.length === 0 || filters.initiatives.includes(init)}
            onToggle={() => toggleFilter('initiatives', init, ALL_INITIATIVES, filters.initiatives, setFilters)}
          />
        ))}
      </DropdownFilter>

      <DropdownFilter
        label="Phase"
        activeCount={filters.phases.length === 0 ? allPhases.length : filters.phases.length}
        totalCount={allPhases.length}
        isOpen={openDropdown === 'phase'}
        onToggle={() => setOpenDropdown(openDropdown === 'phase' ? null : 'phase')}
        onClose={closeAll}
        onAll={() => setFilters({ phases: [] })}
        onNone={() => setFilters({ phases: ['__none__' as Phase] })}
      >
        {allPhases.map((phase) => (
          <FilterChip
            key={phase}
            label={phase}
            color={PHASE_COLORS[phase] ?? '#6b7280'}
            isActive={filters.phases.length === 0 || filters.phases.includes(phase)}
            onToggle={() => toggleFilter('phases', phase, allPhases, filters.phases, setFilters)}
          />
        ))}
      </DropdownFilter>

      <DropdownFilter
        label="Status"
        activeCount={filters.statuses.length === 0 ? allStatuses.length : filters.statuses.length}
        totalCount={allStatuses.length}
        isOpen={openDropdown === 'status'}
        onToggle={() => setOpenDropdown(openDropdown === 'status' ? null : 'status')}
        onClose={closeAll}
        onAll={() => setFilters({ statuses: [] })}
        onNone={() => setFilters({ statuses: ['__none__' as Status] })}
      >
        {allStatuses.map((status) => (
          <FilterChip
            key={status}
            label={status}
            color={STATUS_COLORS[status] ?? STATUS_DEFAULT_COLOR}
            isActive={filters.statuses.length === 0 || filters.statuses.includes(status)}
            onToggle={() => toggleFilter('statuses', status, allStatuses, filters.statuses, setFilters)}
          />
        ))}
      </DropdownFilter>

      <DropdownFilter
        label="Source"
        activeCount={filters.sources.length === 0 ? ALL_SOURCES.length : filters.sources.length}
        totalCount={ALL_SOURCES.length}
        isOpen={openDropdown === 'source'}
        onToggle={() => setOpenDropdown(openDropdown === 'source' ? null : 'source')}
        onClose={closeAll}
        onAll={() => setFilters({ sources: [] })}
        onNone={() => setFilters({ sources: ['__none__' as DataSource] })}
      >
        {ALL_SOURCES.map((source) => (
          <FilterChip
            key={source}
            label={SOURCE_LABELS[source]}
            color={SOURCE_COLORS[source]}
            isActive={filters.sources.length === 0 || filters.sources.includes(source)}
            onToggle={() => toggleFilter('sources', source, ALL_SOURCES, filters.sources, setFilters)}
          />
        ))}
      </DropdownFilter>

      <DropdownFilter
        label="Relations"
        activeCount={filters.relationshipFilter.length === 0 ? ALL_REL_TYPES.length : filters.relationshipFilter.length}
        totalCount={ALL_REL_TYPES.length}
        isOpen={openDropdown === 'rel'}
        onToggle={() => setOpenDropdown(openDropdown === 'rel' ? null : 'rel')}
        onClose={closeAll}
        onAll={() => setFilters({ relationshipFilter: [] })}
        onNone={() => setFilters({ relationshipFilter: ['__none__' as RelationshipType] })}
      >
        {ALL_REL_TYPES.map((rel) => (
          <FilterChip
            key={rel}
            label={REL_TYPE_LABELS[rel]}
            color={REL_TYPE_COLORS[rel]}
            isActive={filters.relationshipFilter.length === 0 || filters.relationshipFilter.includes(rel)}
            onToggle={() => toggleFilter('relationshipFilter', rel, ALL_REL_TYPES, filters.relationshipFilter, setFilters)}
          />
        ))}
      </DropdownFilter>

      {hasActiveFilters && (
        <button
          onClick={resetFilters}
          className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded transition-colors ml-1"
          style={{ color: 'var(--text-muted)' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#f87171'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
        >
          <X size={10} />
          Clear
        </button>
      )}
    </div>
  );
}

// ── DropdownFilter ───────────────────────────────────────────────────

function DropdownFilter({
  label,
  activeCount,
  totalCount,
  isOpen,
  onToggle,
  onClose,
  onAll,
  onNone,
  children,
}: {
  label: string;
  activeCount: number;
  totalCount: number;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onAll: () => void;
  onNone: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  const isFiltered = activeCount !== totalCount;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={onToggle}
        className="flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded transition-colors"
        style={{
          background: isFiltered ? 'var(--filter-pill-active-bg)' : 'var(--filter-pill-bg)',
          color: isFiltered ? 'var(--filter-pill-active-color)' : 'var(--text-muted)',
          border: `1px solid ${isFiltered ? 'var(--filter-pill-active-border)' : 'var(--filter-pill-border)'}`,
        }}
      >
        {label}
        <span className="text-[9px] tabular-nums opacity-70">
          {activeCount}/{totalCount}
        </span>
        <ChevronDown size={10} style={{ transform: isOpen ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }} />
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 z-30 rounded-lg p-2.5 min-w-48 max-w-72"
          style={{
            background: 'var(--filter-dropdown-bg)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: `1px solid var(--filter-dropdown-border)`,
            boxShadow: 'var(--filter-dropdown-shadow)',
          }}
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-semibold" style={{ color: 'var(--text-secondary)' }}>
              {label}
            </span>
            <span className="flex gap-1.5 text-[10px]">
              <span
                onClick={onAll}
                className="cursor-pointer transition-colors"
                style={{ color: 'var(--text-dim)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--hover-link-color)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-dim)'; }}
              >
                All
              </span>
              <span style={{ color: 'var(--text-dim)' }}>&middot;</span>
              <span
                onClick={onNone}
                className="cursor-pointer transition-colors"
                style={{ color: 'var(--text-dim)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--hover-link-color)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-dim)'; }}
              >
                None
              </span>
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Toggle Helper (same logic as MapLegend) ──────────────────────────

function toggleFilter<T>(
  filterKey: string,
  value: T,
  allValues: T[],
  currentValues: T[],
  setFilters: (filters: Record<string, unknown>) => void,
) {
  if (currentValues.length === 0) {
    const newValues = allValues.filter(v => v !== value);
    setFilters({ [filterKey]: newValues });
  } else if (currentValues.includes(value)) {
    const newValues = currentValues.filter(v => v !== value);
    setFilters({ [filterKey]: newValues.length === allValues.length ? [] : newValues });
  } else {
    const newValues = [...currentValues, value];
    setFilters({ [filterKey]: newValues.length === allValues.length ? [] : newValues });
  }
}
